import { useCallback, useEffect, useState } from 'react';
import Dashboard from './pages/Dashboard.tsx';
import { api, type CardPart, type FloorStage, type User } from './api.ts';
import { mayLeave } from './lib/unsaved.ts';
import { initialsOf, logoUrl, useCompany } from './company.ts';
import Icon, { type IconName } from './components/Icon.tsx';
import { cardsForPo, cardsForPoItem, fmt, type CardStub } from './api.ts';
import JobCard from './pages/JobCard.tsx';
import Dispatch from './pages/Dispatch.tsx';
import Login from './pages/Login.tsx';
import Logs from './pages/Logs.tsx';
import Roles from './pages/Roles.tsx';
import OrderDetail from './pages/OrderDetail.tsx';
import Orders from './pages/Orders.tsx';
import Payments from './pages/Payments.tsx';
import PrePress from './pages/PrePress.tsx';
import Production from './pages/Production.tsx';
import Costing from './pages/Costing.tsx';
import Customers from './pages/Customers.tsx';
import Reports from './pages/Reports.tsx';
import Settings from './pages/Settings.tsx';
import Stock from './pages/Stock.tsx';

/**
 * Production is one screen covering every stage -- there is no per-stage
 * route any more. Dispatch's queue route can carry a job number: the hand-off
 * from finishing Packing links straight to that one job pre-searched, rather
 * than landing on the screen and making someone type the number they were
 * just looking at.
 */
export type StaticRoute =
  | 'dashboard' | 'orders' | 'customers' | 'prepress' | 'production'
  | 'dispatch:queue' | 'dispatch:log'
  | 'stock:board' | 'stock:reels' | 'stock:consumables'
  | 'payments:outstanding' | 'payments:log'
  | 'costing'
  | 'reports:sales' | 'reports:companies' | 'reports:production'
  | 'settings:company'
  | 'settings:options' | 'settings:machines' | 'settings:shifts' | 'settings:stages'
  | 'settings:suppliers' | 'settings:consumables' | 'settings:costing-rates'
  | 'settings:users'
  | 'settings:roles'
  | 'settings:logs';

export type Route = StaticRoute | `dispatch:queue:${string}`;

/** A job number to land Dispatch on pre-searched, or null for the plain route. */
const dispatchPrefill = (r: Route): string | null =>
  r.startsWith('dispatch:queue:') ? decodeURIComponent(r.slice('dispatch:queue:'.length)) : null;

interface Leaf {
  route: Route; label: string; icon: IconName;
  /** What you must be allowed to read for this screen to be worth offering. */
  perm: string;
  badge?: number; alert?: boolean;
}
interface Group { key: string; label: string; icon: IconName; children: Leaf[] }
type Entry = Leaf | Group;

const isGroup = (e: Entry): e is Group => 'children' in e;

/**
 * Everything, with Production's badge summed from the stage list the server
 * sends -- the dispatch stage excluded, since it is never shown or acted on
 * here, only on the Dispatch screen.
 */
const navFor = (stages: FloorStage[]): Entry[] => {
  const floor = stages.filter((s) => !s.is_dispatch);
  return [
  { route: 'dashboard', label: 'Dashboard', icon: 'dashboard', perm: 'dashboard.read' },
  { route: 'orders', label: 'Orders', icon: 'orders', perm: 'orders.read' },
  { route: 'customers', label: 'Customers', icon: 'users', perm: 'customers.read' },
  { route: 'prepress', label: 'Pre-Press', icon: 'prepress', perm: 'prepress.read' },
  {
    route: 'production', label: 'Production', icon: 'production', perm: 'production.read',
    badge: floor.reduce((n, s) => n + s.jobs, 0),
    alert: floor.some((s) => s.late > 0),
  },
  {
    key: 'dispatch', label: 'Dispatch', icon: 'dispatch',
    children: [
      { route: 'dispatch:queue', label: 'To dispatch', icon: 'truck', perm: 'dispatch.read' },
      { route: 'dispatch:log', label: 'Shipment log', icon: 'list', perm: 'dispatch.read' },
    ],
  },
  {
    key: 'stock', label: 'Stock', icon: 'stock',
    children: [
      { route: 'stock:board', label: 'Board', icon: 'box', perm: 'stock_board.read' },
      { route: 'stock:reels', label: 'Reels', icon: 'reel', perm: 'stock_reels.read' },
      { route: 'stock:consumables', label: 'Other stock', icon: 'drop', perm: 'stock_consumables.read' },
    ],
  },
  {
    key: 'payments', label: 'Payments', icon: 'payments',
    children: [
      { route: 'payments:outstanding', label: 'Due to us', icon: 'building', perm: 'payments.read' },
      { route: 'payments:log', label: 'Payment log', icon: 'list', perm: 'payments.read' },
    ],
  },
  { route: 'costing', label: 'Costing', icon: 'chart', perm: 'costing.read' },
  {
    key: 'reports', label: 'Reports', icon: 'reports',
    children: [
      { route: 'reports:sales', label: 'Sales', icon: 'chart', perm: 'reports.read' },
      { route: 'reports:companies', label: 'By company', icon: 'building', perm: 'reports.read' },
      { route: 'reports:production', label: 'Production', icon: 'clock', perm: 'reports.read' },
    ],
  },
  {
    key: 'settings', label: 'Settings', icon: 'settings',
    children: [
      { route: 'settings:company', label: 'Company', icon: 'building', perm: 'settings_company.manage' },
      { route: 'settings:options', label: 'Form options', icon: 'sliders', perm: 'settings_options.read' },
      { route: 'settings:machines', label: 'Machines', icon: 'production', perm: 'settings_machines.read' },
      { route: 'settings:shifts', label: 'Shifts', icon: 'clock', perm: 'settings_shifts.read' },
      { route: 'settings:stages', label: 'Production route', icon: 'list', perm: 'settings_stages.read' },
      { route: 'settings:suppliers', label: 'Suppliers', icon: 'building', perm: 'settings_suppliers.read' },
      { route: 'settings:consumables', label: 'Consumables', icon: 'drop', perm: 'settings_consumables.read' },
      { route: 'settings:costing-rates', label: 'Costing rates', icon: 'chart', perm: 'settings_costing_rates.read' },
      { route: 'settings:users', label: 'Users', icon: 'users', perm: 'settings_users.read' },
      { route: 'settings:roles', label: 'Roles', icon: 'sliders', perm: 'settings_roles.read' },
      { route: 'settings:logs', label: 'Logs', icon: 'clock', perm: 'logs.read' },
    ],
  },
  ];
};

/**
 * The menu, cut down to what this person is actually allowed to open.
 *
 * Without this the sidebar offered every screen to everybody and the refusal
 * came afterwards, as a red error on a page that had already opened -- so a
 * floor operator was shown Users and Roles, clicked them, and was told off.
 * Roles are only worth having if they change what the app looks like; a menu
 * of locked doors is worse than a short menu.
 *
 * This hides, it does not protect. Every one of these screens is guarded again
 * at its endpoint, which is the check that actually matters -- this one is so
 * nobody is offered a door that will not open.
 */
function visibleNav(stages: FloorStage[], can: (p: string) => boolean): Entry[] {
  return navFor(stages)
    .map((e) => (isGroup(e) ? { ...e, children: e.children.filter((c) => can(c.perm)) } : e))
    .filter((e) => (isGroup(e) ? e.children.length > 0 : can(e.perm)));
}

const TITLES: Record<StaticRoute, [string, string]> = {
  dashboard: ['Dashboard', 'Where everything stands today'],
  orders: ['Sales orders', 'Every order line and how far it has got'],
  customers: ['Customers', 'Companies you sell to, and how they are doing'],
  prepress: ['Pre-Press', 'Stage today’s work, then release it'],
  production: ['Production', 'Every stage, one screen'],
  'dispatch:queue': ['Dispatch', 'Finished jobs waiting to go out'],
  'dispatch:log': ['Shipment log', 'Everything that has left'],
  'stock:board': ['Board', 'Sheets in stock, and every one in and out'],
  'stock:reels': ['Reels', 'Full and Half reels in hand'],
  'stock:consumables': ['Other stock', 'Gum, pins, chemicals and ink'],
  'payments:outstanding': ['Due to us', 'Shipped less received, per company'],
  'payments:log': ['Payment log', 'Money received'],
  'reports:sales': ['Sales', 'Ordered and dispatched by period'],
  'reports:companies': ['By company', 'All-time activity per customer'],
  'reports:production': ['Production', 'Output by stage, shift and day'],
  'settings:company': ['Company', 'The name and mark on the app and every printed sheet'],
  'settings:options': ['Form options', 'The lists that fill job cards and orders'],
  'settings:machines': ['Machines', 'Recorded against every production entry'],
  'settings:shifts': ['Shifts', 'And which day a night shift counts against'],
  'settings:stages': ['Production route', 'The factory sequence, in order'],
  'settings:suppliers': ['Suppliers', 'Who board and consumables come from'],
  costing: ['Costing', 'What a box costs to make, before it is quoted'],
  'settings:consumables': ['Consumables', 'Gum, pins, ink and the rest'],
  'settings:costing-rates': ['Costing rates', 'What a new costing starts from'],
  'settings:users': ['Users', 'Who can sign in, and what they may reach'],
  'settings:roles': ['Roles', 'What each role is allowed to do'],
  'settings:logs': ['Logs', 'Who changed what, and when'],
};

/**
 * Dispatch's pre-searched route carries a job number the title bar has no use
 * for; strip it back to the plain route before looking up a title.
 */
function titleFor(route: Route): readonly [string, string] {
  const plain = dispatchPrefill(route) !== null ? 'dispatch:queue' : route;
  return TITLES[plain as StaticRoute];
}

/** Which group a route belongs to, so it opens on load. */
const groupOf = (r: Route) => (r.includes(':') ? r.split(':')[0]! : null);

/**
 * Narrow screens get a drawer instead of a permanent sidebar. Driven by the
 * same breakpoint as the stylesheet, so the two cannot disagree about which
 * layout is in force.
 */
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 820px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)');
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

export default function App() {
  const narrow = useIsNarrow();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [checked, setChecked] = useState(false);
  const [route, setRoute] = useState<Route>('dashboard');
  const company = useCompany();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('opal.sidebar');
    if (saved) return saved === 'collapsed';
    return typeof window !== 'undefined' && window.innerWidth < 1180;
  });
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('opal.theme') === 'light' ? 'light' : 'dark'),
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});
  // Production's menu is the stage table, so it is fetched rather than written
  // out here, and re-fetched whenever the floor moves so the counts beside each
  // stage stay true.
  const [stages, setStages] = useState<FloorStage[]>([]);
  // A job card overlays whatever screen you came from, so Back returns there.
  const [cardJobId, setCardJobId] = useState<number | null>(null);
  // Which of its two cards to open at (067): the one the stage works from.
  const [cardPart, setCardPart] = useState<CardPart | undefined>(undefined);
  /** Opened to fix it: straight into editing, and Save comes back here. */
  const [cardEdit, setCardEdit] = useState(false);
  /** What was just done on a card opened to fix it, shown on the list it came from. */
  const [flash, setFlash] = useState('');
  const openJob = (jobId: number, part?: CardPart, edit = false) => {
    setFlash(''); setCardPart(part); setCardEdit(edit); setCardJobId(jobId);
  };
  // The order View page, an overlay over the route in the same way the job
  // card is. Clearing both is what go() does on any nav click.
  const [orderId, setOrderId] = useState<number | null>(null);
  const [orderLine, setOrderLine] = useState<number | null>(null);
  const [picker, setPicker] = useState<CardStub[] | null>(null);
  const [cardError, setCardError] = useState('');
  // Writing the card is the step after taking the order, so a PO item with no
  // card offers to make one rather than reporting a dead end.

  /**
   * A PO or PO item can have several jobs, so resolve first: open the card
   * directly when there is one, offer a choice when there are more, and say
   * plainly when there are none rather than opening an empty card.
   */
  async function openCardFor(kind: 'po' | 'item', id: number) {
    setCardError('');
    try {
      const res = kind === 'po' ? await cardsForPo(id) : await cardsForPoItem(id);
      if (res.cards.length === 0) {
        if (kind === 'item') {
          // No dialog asking how much. The quantity was given when the order
          // was placed, and every line already gets a card then -- this path is
          // only reached for the remainder after a card was cut down, where the
          // answer is "whatever is left". It can still be edited on the card.
          await createCardFor(id);
        } else {
          setCardError('No job card on this order yet. Open the order and make one per line.');
        }
        return;
      }
      if (res.cards.length === 1) { openJob(res.cards[0]!.job_id); return; }
      setPicker(res.cards);
    } catch {
      setCardError('Could not look up the job card.');
    }
  }

  /*
   * Land somewhere you can actually open. Dashboard is where everybody starts,
   * but a role can be defined without it -- and a role edited while somebody is
   * signed in can take the screen they are on out from under them. Either way
   * the answer is the same: move to the first screen they are allowed.
   */
  useEffect(() => {
    if (!user) return;
    const allowed = visibleNav(stages, (p) => user.permissions?.includes(p) ?? false);
    const reachable = allowed.flatMap((e) => (isGroup(e) ? e.children : [e])).map((l) => l.route);
    // Dispatch's pre-searched route carries a job number the static nav list
    // never contains literally -- check the plain route it stands for, same
    // normalization titleFor already does, or this kicks back to Dashboard
    // the instant the hand-off link is used.
    const plain = dispatchPrefill(route) !== null ? 'dispatch:queue' : route;
    if (reachable.length > 0 && !reachable.includes(plain)) setRoute(reachable[0]!);
  }, [user, stages, route]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('opal.theme', theme);
  }, [theme]);

  const toggleSidebar = useCallback(() => {
    if (narrow) { setDrawerOpen((v) => !v); return; }
    setCollapsed((v) => {
      localStorage.setItem('opal.sidebar', v ? 'open' : 'collapsed');
      return !v;
    });
  }, [narrow]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      toggleSidebar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleSidebar]);

  const loadStages = useCallback(() => {
    if (!user) return;
    api
      .get<{ stages: FloorStage[] }>('/api/production/stages')
      .then((r) => setStages(r.stages))
      // A menu that cannot count is still a usable menu, so this stays quiet
      // rather than throwing an error banner over whatever screen you are on.
      .catch(() => {});
  }, [user]);

  useEffect(loadStages, [loadStages]);

  useEffect(() => {
    api
      .get<{ user: User }>('/api/auth/me')
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setChecked(true));
  }, []);

  function go(r: Route) {
    // A screen in the middle of unsaved work gets to object first; see
    // lib/unsaved.ts for why this is not a router concern.
    if (!mayLeave()) return;
    setRoute(r);
    setFlash('');
    const g = groupOf(r);
    if (g) setOpen((p) => ({ ...p, [g]: true }));
    // The job card is an overlay on top of whatever route is active. Leaving it
    // up after a nav click stranded you on the card with the title and sidebar
    // both claiming you had moved on.
    setCardJobId(null);
    setOrderId(null);
    setPicker(null);
    setCardError('');
    // On a phone the drawer covers the content it just navigated to.
    if (narrow) setDrawerOpen(false);
  }

  /**
   * Raise a card for whatever quantity on this line no job covers, and open it.
   *
   * The server works the remainder out; sending a figure from here would be a
   * second opinion about a number the database already knows, and the two would
   * disagree the moment somebody else released a card first.
   */
  async function createCardFor(poItemId: number) {
    setCardError('');
    try {
      const res = await api.post<{ job: { id: number } }>(
        `/api/pre-press/po-items/${poItemId}/job-cards`, {},
      );
      openJob(res.job.id);
    } catch (err) {
      setCardError(err instanceof Error ? err.message : 'Could not create the job card');
    }
  }

  async function signOut() {
    await api.post('/api/auth/logout').catch(() => {});
    setUser(null);
  }

  if (!checked) return <div className="empty">Loading…</div>;
  if (!user) return <Login onSignedIn={setUser} />;

  const can = (perm: string) => user.permissions?.includes(perm) ?? false;
  const nav = visibleNav(stages, can);

  const [title, subtitle] = cardJobId
    ? (['Job card', 'Printing and corrugation, one document'] as const)
    : orderId !== null
      ? (['Order', 'Every line, and everything that has happened to it'] as const)
      : titleFor(route);
  const initials = user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

  return (
    <div className="shell">
      {narrow && drawerOpen && (
        <div className="drawer-scrim" onClick={() => setDrawerOpen(false)} />
      )}

      <aside
        className={[
          'sidebar',
          !narrow && collapsed ? 'collapsed' : '',
          narrow && drawerOpen ? 'drawer-open' : '',
        ].filter(Boolean).join(' ')}
      >
        {company.hasLogo ? (
          <div className="brand wordmark">
            <img src={logoUrl(company)} alt={company.name} />
          </div>
        ) : (
          <div className="brand">
            <div className="brand-mark">{initialsOf(company.name)}</div>
            <div className="brand-text">
              <strong>{company.name}</strong>
              <span>{company.tagline}</span>
            </div>
          </div>
        )}

        <nav className="nav">
          {nav.map((entry) => {
            if (!isGroup(entry)) {
              return (
                <button key={entry.route} data-tip={entry.label}
                        className={`nav-item${route === entry.route ? ' on' : ''}`}
                        onClick={() => go(entry.route)}>
                  <Icon name={entry.icon} />
                  <span className="nav-label">{entry.label}</span>
                  {entry.badge !== undefined && entry.badge > 0 && (
                    <span className={`nav-count${entry.alert ? ' alert' : ''}`}
                          title={entry.alert ? 'Some of these are late' : undefined}>
                      {entry.badge}
                    </span>
                  )}
                </button>
              );
            }

            const expanded = open[entry.key] ?? groupOf(route) === entry.key;
            const active = groupOf(route) === entry.key;
            return (
              <div key={entry.key} className="nav-group">
                <button className="nav-group-btn" aria-label={entry.label}
                        onClick={() => setOpen((p) => ({ ...p, [entry.key]: !expanded }))}>
                  <Icon name={entry.icon} />
                  {entry.children.some((c) => (c.badge ?? 0) > 0) && (
                    <span className={`nav-dot${entry.children.some((c) => c.alert && (c.badge ?? 0) > 0) ? ' alert' : ''}`} />
                  )}
                  <span className={`nav-label${active ? ' active' : ''}`}>
                    {entry.label}
                  </span>
                  <Icon name="chevron" size={14}
                        className={`nav-chevron${expanded ? ' open' : ''}`} />
                </button>
                {(expanded || (!narrow && collapsed)) && (
                  <div className="nav-children">
                    <div className="nav-fly-title">{entry.label}</div>
                    {entry.children.map((c) => (
                      <button key={c.route}
                              className={`nav-child${
                                (dispatchPrefill(route) !== null ? 'dispatch:queue' : route) === c.route
                                  ? ' on' : ''
                              }`}
                              onClick={() => go(c.route)}>
                        <Icon name={c.icon} size={15} />
                        <span className="nav-label">{c.label}</span>
                        {c.badge !== undefined && c.badge > 0 && (
                          <span className={`nav-count${c.alert ? ' alert' : ''}`}
                                title={c.alert ? 'Some of these are late' : undefined}>
                            {c.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="icon-btn"
                  title={narrow ? 'Menu' : 'Collapse or expand menu  ['}
                  aria-label={narrow ? 'Menu' : 'Toggle sidebar'}
                  onClick={toggleSidebar}>
            <Icon name={narrow ? 'list' : 'panel'} />
          </button>
          {!(route === 'dashboard' && cardJobId === null && orderId === null) && (
            <div>
              <h1>{title}</h1>
              <div className="sub">{subtitle}</div>
            </div>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="icon-btn"
                    title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            </button>
            <div className="avatar" title={`${user.name} · ${user.role}`}>{initials}</div>
            <button className="icon-btn" title="Sign out" onClick={signOut}>
              <Icon name="logout" />
            </button>
          </div>
        </header>

        <main className="content">
          {flash && cardJobId === null && (
            <div className="tag ready notice no-print" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ flex: '1 1 auto' }}>{flash}</span>
              <button className="linkish" onClick={() => setFlash('')}>Dismiss</button>
            </div>
          )}
          {cardError && (
            <div className="error no-print" style={{ marginBottom: 14 }}>
              {/* Flex, not inline text: as plain flow the message wrapped around
                  the button and broke mid-sentence on a narrow screen. */}
              <span style={{ flex: '1 1 auto' }}>{cardError}</span>
              <button className="btn" onClick={() => setCardError('')}>Dismiss</button>
            </div>
          )}

          {picker && (
            <div className="backdrop" onClick={() => setPicker(null)}>
              <div className="modal" style={{ width: 480 }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-head">
                  <h3>Which job card?</h3>
                  <p>This order has been released as more than one job.</p>
                </div>
                <div className="modal-body" style={{ paddingTop: 8 }}>
                  {picker.map((c) => (
                    <button key={c.job_id} className="btn"
                            style={{ display: 'block', width: '100%', textAlign: 'left',
                                     marginBottom: 8 }}
                            onClick={() => { openJob(c.job_id); setPicker(null); }}>
                      <strong>{c.job_number}</strong>
                      {c.item_name ? ` · ${c.item_name}` : ''} · {fmt(c.production_quantity)}
                      {c.has_artwork ? ' · artwork' : ''}
                    </button>
                  ))}
                </div>
                <div className="modal-foot">
                  <button className="btn" onClick={() => setPicker(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {cardJobId !== null ? (
            <JobCard key={`${cardJobId}:${cardPart ?? ''}:${cardEdit}`} jobId={cardJobId} part={cardPart}
                     startEditing={cardEdit} user={user}
                     onBack={(msg) => { setCardJobId(null); if (msg) setFlash(msg); }} />
          ) : orderId !== null ? (
            <OrderDetail poId={orderId} focusItemId={orderLine}
                         onBack={() => setOrderId(null)}
                         onOpenCard={(id) => openCardFor('item', id)}
                         onOpenJob={(id) => openJob(id)} />
          ) : (
          <>
          {route === 'dashboard' && <Dashboard stages={stages} go={go} />}
          {route === 'customers' && <Customers user={user} />}
          {route === 'orders' && (
            <Orders user={user}
                    onOpenOrder={(poId, itemId) => { setOrderLine(itemId ?? null); setOrderId(poId); }}
                    onOpenJob={(id) => openJob(id)} />
          )}
          {route === 'prepress' && (
            <PrePress user={user}
                      onOpenCard={(id) => openCardFor('item', id)}
                      onOpenJob={openJob}
                      onGoBoard={() => go('stock:board')} />
          )}
          {route === 'production' && (
            <Production stages={stages}
                        onOpenCard={openJob}
                        onChanged={loadStages}
                        onDispatchReady={(jobNumber) => go(`dispatch:queue:${encodeURIComponent(jobNumber)}`)} />
          )}
          {route.startsWith('dispatch:') && (
            <Dispatch user={user}
                      view={route === 'dispatch:log' ? 'log' : 'queue'}
                      prefillSearch={dispatchPrefill(route) ?? undefined} />
          )}
          {route.startsWith('stock:') && (
            <Stock user={user}
                   view={route === 'stock:reels' ? 'reels'
                       : route === 'stock:consumables' ? 'consumables' : 'board'} />
          )}
          {route.startsWith('payments:') && (
            <Payments user={user} view={route === 'payments:log' ? 'log' : 'outstanding'} />
          )}
          {route === 'costing' && <Costing user={user} />}
          {route.startsWith('reports:') && (
            <Reports view={route === 'reports:companies' ? 'companies'
                        : route === 'reports:production' ? 'production' : 'sales'} />
          )}
          {route === 'settings:logs' && <Logs />}
          {route === 'settings:roles' && <Roles user={user} />}
          {route.startsWith('settings:') && route !== 'settings:logs'
            && route !== 'settings:roles' && (
            <Settings user={user}
                      view={route === 'settings:company' ? 'company'
                          : route === 'settings:machines' ? 'machines'
                          : route === 'settings:shifts' ? 'shifts'
                          : route === 'settings:stages' ? 'stages'
                          : route === 'settings:suppliers' ? 'suppliers'
                          : route === 'settings:consumables' ? 'consumables'
                          : route === 'settings:costing-rates' ? 'costing-rates'
                          : route === 'settings:users' ? 'users' : 'options'} />
          )}
          </>
          )}
        </main>
      </div>
    </div>
  );
}
